/**
 * ParkingStatusDashboard Component
 *
 * Displays real-time parking occupancy status per zone.
 */

import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
} from '@mui/material';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import { useParkingCurrentStatus } from '@/hooks/useParking';

export function ParkingStatusDashboard() {
  const { t } = useTranslation('equipment');
  const { data: status, isLoading, error } = useParkingCurrentStatus();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{String(error)}</Alert>;
  }

  if (!status || status.zones.length === 0) {
    return (
      <Alert severity="info" icon={<LocalParkingIcon />}>
        <Typography variant="body2" fontWeight={600}>
          {t('parkingStatus.noZones')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('parkingStatus.noZonesDesc')}
        </Typography>
      </Alert>
    );
  }

  const overallPercent = status.overall.totalSpots > 0
    ? Math.round((status.overall.occupied / status.overall.totalSpots) * 100)
    : 0;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h6">{t('parkingStatus.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('parkingStatus.subtitle')}
          </Typography>
        </Box>
        <Chip
          label={t('parkingStatus.occupiedOf', {
            occupied: status.overall.occupied,
            total: status.overall.totalSpots,
          })}
          color={overallPercent > 80 ? 'error' : overallPercent > 50 ? 'warning' : 'success'}
        />
      </Stack>

      <Grid container spacing={2}>
        {status.zones.map((zone) => {
          const percent = zone.totalSpots > 0
            ? Math.round((zone.occupiedCount / zone.totalSpots) * 100)
            : 0;
          const progressColor = percent > 80 ? 'error' : percent > 50 ? 'warning' : 'success';

          return (
            <Grid key={zone.zoneId} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {zone.zoneName}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${zone.occupiedCount} / ${zone.totalSpots}`}
                      color={progressColor}
                      variant="outlined"
                    />
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    color={progressColor}
                    sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
                  />

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {zone.spots.map((spot) => (
                      <Tooltip
                        key={spot.spotId}
                        title={spot.isOccupied && spot.vehicleReg ? spot.vehicleReg : ''}
                        arrow
                      >
                        <Chip
                          size="small"
                          label={spot.identifier}
                          color={spot.isOccupied ? 'error' : 'success'}
                          variant={spot.isOccupied ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
